import { firstUpperCaseLetter } from '@/lib/utils';
import React, { useCallback } from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { has } from 'lodash';
import { Wallet } from 'lucide-react';
import { useSelector } from 'react-redux';
import { translate } from '@/services/TranslationService';
import { Typography } from '@/components/novoville';

dayjs.extend(utc);
dayjs.extend(timezone);

const SubmittedApplicationsItem = ({ application }) => {
  const { title, created_at, status, status_color, payment } = application;

  const modules = useSelector((state) => state.otherReducer.modules);

  const isPaymentModuleEnabled = modules.map((m) => m.name).includes('bills');

  const createdAt = dayjs(created_at).format('DD/MM/YYYY');

  const statusValues = Object.values(status)[0];
  const statusColorValues = Object.values(status_color)[0];

  const hasPaymentPending = !!payment && Object.keys(payment).length > 0;

  const paymentObjectKey = hasPaymentPending ? Object.keys(payment)[0] : null;

  const paymentAmount = hasPaymentPending
    ? payment[paymentObjectKey].amount_in_cents
    : null;

  const isPaymentAmountZero = paymentAmount === 0;

  const isPaymentPaid = hasPaymentPending
    ? payment[paymentObjectKey].paid === 1
    : false;

  const paymentCode = hasPaymentPending ? payment[paymentObjectKey].code : null;

  const handlePaymentClick = useCallback(() => {
    if (
      !hasPaymentPending ||
      isPaymentPaid ||
      !paymentCode ||
      !isPaymentModuleEnabled
    )
      return;

    const paymentUrl = `/payments/${paymentCode}`;
    window.open(paymentUrl);

    return;
  }, [hasPaymentPending, isPaymentPaid, paymentCode, isPaymentModuleEnabled]);

  return (
    <div
      className={`flex flex-col w-full bg-white p-8 mt-4 drop-shadow-sm rounded-sm 
          ${hasPaymentPending ? 'space-y-4' : 'space-y-0'}
        `}
    >
      {hasPaymentPending && !isPaymentAmountZero && !isPaymentPaid && (
        <div
          onClick={handlePaymentClick}
          className={`inline-flex rounded-lg px-4 py-3 text-center mr-4 bg-[#FF9C4033] cursor-pointer drop-shadow-sm hover:drop-shadow-none`}
        >
          <Wallet className="inline-block w-4 *:w-full h-4 mr-2" size={16} />
          <Typography variant="regular_12" className="text-gray-800">
            {`${translate('Requires payment')}. ${translate('Please click here to proceed to payment')}.`}
          </Typography>
        </div>
      )}
      {hasPaymentPending && isPaymentAmountZero && !isPaymentPaid && (
        <div
          onClick={handlePaymentClick}
          className={`inline-flex rounded-lg px-4 py-3 text-center mr-4 bg-[#FF9C4033] cursor-pointer drop-shadow-sm hover:drop-shadow-none`}
        >
          <Wallet
            className="inline-block min-w-4 *:w-full h-4 mr-2"
            size={16}
          />
          <Typography variant="regular_12" className="text-gray-800">
            {`${translate('Requires completion')}. ${translate('Please click here to finalize the procedure')}.`}
          </Typography>
        </div>
      )}

      <div className="flex sm:flex-row flex-col-reverse space-y-3 justify-between item-center">
        <div className="flex flex-col space-y-4">
          <Typography variant="bold_16" className="text-gray-800">
            {title}
          </Typography>
          <Typography variant={'regular_12'} className="text-gray-700">
            {`${translate('Submitted at')} ${createdAt}`}
          </Typography>
        </div>
        <div
          className={`inline-flex mb-2 sm:mb-0 items-center rounded-full w-fit my-auto px-4 py-2 text-center`}
          style={{ backgroundColor: statusColorValues }}
        >
          <Typography variant="regular_12">
            {firstUpperCaseLetter(
              translate(String(statusValues).toLowerCase())
            )}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default SubmittedApplicationsItem;
